import { useState, type ReactNode } from 'react';
import { BookOpen, Car, ChevronRight, Flame, Gauge, Layers, Play, RotateCcw, Target, Timer, Trophy, type LucideIcon } from 'lucide-react';
import dayjs from '../../lib/dates';
import { Sheet } from '../../components/shell/Sheet';
import { Badge, Button, Callout, Card, EmptyState, Eyebrow, Segmented, cx } from '../../components/charte';
import {
  MODE_LABELS,
  minutesLabel,
  seriesTitle,
  weakestThemes,
  type RoadCodeOverview,
  type RoadCodeThemeKey,
  type SeriesFeedback,
  type SeriesStart,
} from '../../lib/roadCode';
import { ThemeRow, ThemeSheet } from './ThemeSheet';

/** Chiffre de la semaine : petite tuile, trois côte à côte même sur téléphone. */
function Stat({ icon: Icon, value, label, tone = 'text-primary' }: { icon: LucideIcon; value: ReactNode; label: string; tone?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-[20px] bg-white p-3.5 shadow-card sm:p-4">
      <Icon size={18} strokeWidth={1.9} className={tone} aria-hidden />
      <span className="text-[22px] font-semibold leading-tight tracking-[-1px] text-heading tabular-nums sm:text-[26px]">{value}</span>
      <span className="text-[11.5px] leading-tight text-secondary sm:text-xs">{label}</span>
    </div>
  );
}

/** Outil de révision : grande tuile tactile. */
function Tool({
  icon: Icon,
  title,
  detail,
  onClick,
  disabled,
  tile,
}: {
  icon: LucideIcon;
  title: string;
  detail: string;
  onClick: () => void;
  disabled?: boolean;
  tile: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="group flex min-h-[132px] flex-col justify-between rounded-card bg-white p-4 text-left shadow-card transition-all enabled:hover:-translate-y-0.5 enabled:hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className={cx('grid h-11 w-11 place-items-center rounded-2xl', tile)}>
        <Icon size={22} strokeWidth={1.9} aria-hidden />
      </span>
      <span className="mt-3 flex items-end justify-between gap-2">
        <span className="min-w-0">
          <span className="block text-[15px] font-semibold leading-snug text-heading">{title}</span>
          <span className="mt-0.5 block text-[12px] text-secondary">{detail}</span>
        </span>
        <ChevronRight size={16} className="shrink-0 text-muted group-enabled:group-hover:text-primary" aria-hidden />
      </span>
    </button>
  );
}

/** Objectif de l'examen blanc : 5 réussis d'affilée, en pastilles. */
function ExamStreak({ streak, goal }: { streak: number; goal: number }) {
  return (
    <span className="flex gap-1" aria-hidden>
      {Array.from({ length: goal }, (_, i) => (
        <span key={i} className={cx('h-1.5 flex-1 rounded-pill', i < streak ? 'bg-success' : 'bg-surface-2')} />
      ))}
    </span>
  );
}

/**
 * Accueil de l'onglet Code : semaine de révision, série à reprendre, outils (séries thématiques,
 * entraînement, examen blanc, fiches), erreurs à revoir, thèmes à travailler en priorité et derniers
 * résultats. Mobile d'abord : tuiles tactiles, une seule action principale.
 */
export function CodeHome({
  overview,
  starting,
  startError,
  onStart,
  onResume,
  onOpenLessons,
  onOpenResult,
}: {
  overview: RoadCodeOverview;
  starting: boolean;
  startError: string | null;
  onStart: (start: SeriesStart) => void;
  onResume: (seriesId: string) => void;
  onOpenLessons: () => void;
  onOpenResult: (seriesId: string) => void;
}) {
  const [sheet, setSheet] = useState<'themes' | 'training' | 'exam' | null>(null);
  const [feedback, setFeedback] = useState<SeriesFeedback>('each');
  const progress = overview.progress;
  const active = overview.access.status === 'active';
  const hasQuestions = overview.bank.questions > 0;
  const canTrain = active && hasQuestions;
  const themes = progress?.themes ?? [];
  const weakest = weakestThemes(themes);
  const start = (value: SeriesStart) => {
    setSheet(null);
    onStart(value);
  };

  return (
    <div className="grid gap-5 sm:gap-6">
      <div>
        <h1 className="text-title-md text-heading sm:text-title-lg">Code</h1>
        <p className="mt-1 text-sm text-secondary">Révisez l’examen du code à votre rythme, avec l’accès ouvert par votre auto-école.</p>
        {active && overview.access.endsAt && (
          <p className="mt-0.5 text-[12.5px] text-muted">Accès jusqu’au {dayjs(overview.access.endsAt).format('D MMMM YYYY')}</p>
        )}
      </div>

      {overview.access.status === 'ended' && (
        <Callout tone="warn">
          Votre accès à la révision du code a pris fin
          {overview.access.endsAt ? ` le ${dayjs(overview.access.endsAt).format('D MMMM YYYY')}` : ''}. Demandez à votre auto-école de le
          renouveler : votre progression est gardée.
        </Callout>
      )}
      {startError && (
        <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-[13.5px] text-danger-dark">
          {startError}
        </p>
      )}

      {progress && (
        <div className="grid grid-cols-3 gap-2.5 sm:gap-4">
          <Stat icon={Timer} value={minutesLabel(progress.week.minutes)} label="cette semaine" />
          <Stat
            icon={Flame}
            value={progress.dayStreak}
            label={progress.dayStreak > 1 ? 'jours d’affilée' : 'jour d’affilée'}
            tone="text-amber-600"
          />
          <div className="flex min-w-0 flex-col gap-1 rounded-[20px] bg-white p-3.5 shadow-card sm:p-4">
            <Trophy size={18} strokeWidth={1.9} className="text-success" aria-hidden />
            <span className="text-[22px] font-semibold leading-tight tracking-[-1px] text-heading tabular-nums sm:text-[26px]">
              {progress.exams.streak}
              <small className="text-[14px] font-medium text-muted">/{progress.exams.goal}</small>
            </span>
            <ExamStreak streak={progress.exams.streak} goal={progress.exams.goal} />
            <span className="text-[11.5px] leading-tight text-secondary sm:text-xs">
              {progress.exams.ready ? 'Prêt pour l’examen !' : 'examens blancs réussis d’affilée'}
            </span>
          </div>
        </div>
      )}

      {/* Série en cours : l'action principale de l'écran. */}
      {active && progress?.current && (
        <Card className="flex items-center gap-4 max-sm:flex-wrap">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary-50 text-primary">
            <Play size={22} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <Eyebrow>Série en cours</Eyebrow>
            <p className="mt-0.5 font-semibold text-heading">{seriesTitle(progress.current.mode, progress.current.theme, overview.themes)}</p>
            <p className="text-[13px] text-secondary">
              {progress.current.answered} question{progress.current.answered > 1 ? 's' : ''} sur {progress.current.total}
            </p>
          </div>
          <Button variant="primary" className="max-sm:w-full" onClick={() => onResume(progress.current!.id)}>
            Reprendre
          </Button>
        </Card>
      )}

      {!hasQuestions && (
        <EmptyState
          icon={Layers}
          title="Les questions arrivent bientôt"
          description="La banque de questions est en cours de préparation. Vous serez prévenu dès son ouverture."
        />
      )}

      <section aria-label="Outils de révision" className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Tool
          icon={Target}
          title="Séries thématiques"
          detail="10 questions par thème"
          tile="bg-primary-50 text-primary-600"
          disabled={!canTrain}
          onClick={() => setSheet('themes')}
        />
        <Tool
          icon={Gauge}
          title="Entraînement"
          detail="20 questions variées"
          tile="bg-blue-100 text-blue-800"
          disabled={!canTrain}
          onClick={() => setSheet('training')}
        />
        <Tool
          icon={Car}
          title="Examen blanc"
          detail="40 questions · 20 s"
          tile="bg-amber-100 text-amber-700"
          disabled={!canTrain}
          onClick={() => setSheet('exam')}
        />
        <Tool
          icon={BookOpen}
          title="Fiches de cours"
          detail={overview.bank.lessons ? `${overview.bank.lessons} fiche${overview.bank.lessons > 1 ? 's' : ''}` : 'Bientôt'}
          tile="bg-success-light text-success-dark"
          disabled={!active || !overview.bank.lessons}
          onClick={onOpenLessons}
        />
      </section>

      {canTrain && !!progress?.mistakes && (
        <button
          type="button"
          onClick={() => start({ mode: 'mistakes' })}
          disabled={starting}
          className="flex items-center gap-3 rounded-card border border-danger/20 bg-danger-light p-4 text-left transition hover:shadow-navbar focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-danger/20 disabled:opacity-60"
        >
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-white text-danger">
            <RotateCcw size={20} aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-heading">Revoir mes erreurs</span>
            <span className="block text-[13px] text-secondary">
              {progress.mistakes} question{progress.mistakes > 1 ? 's' : ''} ratée{progress.mistakes > 1 ? 's' : ''} à retravailler
            </span>
          </span>
          <ChevronRight size={16} className="text-muted" aria-hidden />
        </button>
      )}

      {canTrain && weakest.length > 0 && (
        <section aria-labelledby="code-priorites">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 id="code-priorites" className="text-title-sm text-heading">
              À travailler en priorité
            </h2>
            <Button variant="link" size="sm" onClick={() => setSheet('themes')}>
              Tous les thèmes
            </Button>
          </div>
          <ul className="grid gap-2 lg:grid-cols-3">
            {weakest.map((theme) => (
              <li key={theme.theme}>
                <ThemeRow theme={theme} disabled={starting} onPick={() => start({ mode: 'thematic', theme: theme.theme })} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {!!progress?.recent.length && (
        <Card flush aria-labelledby="code-resultats">
          <h2 id="code-resultats" className="px-5 pb-2 pt-5 text-title-sm text-heading">
            Derniers résultats
          </h2>
          <ul className="divide-y divide-line">
            {progress.recent.slice(0, 5).map((series) => (
              <li key={series.id}>
                <button
                  type="button"
                  onClick={() => onOpenResult(series.id)}
                  className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-surface-light focus-visible:bg-surface-light focus-visible:outline-none"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14.5px] font-medium text-heading">{seriesTitle(series.mode, series.theme, overview.themes)}</span>
                    <span className="block text-[12.5px] text-secondary">
                      {series.mode !== 'thematic' ? '' : `${MODE_LABELS.thematic} · `}
                      {dayjs(series.finishedAt).format('D MMM, HH:mm')}
                    </span>
                  </span>
                  <Badge tone={series.mode === 'exam' ? (series.passed ? 'ok' : 'danger') : 'mute'} size="lg" className="normal-case tracking-normal">
                    {series.correct}/{series.total}
                  </Badge>
                  <ChevronRight size={16} className="shrink-0 text-muted" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {sheet === 'themes' && (
        <ThemeSheet themes={themes} onClose={() => setSheet(null)} onPick={(theme: RoadCodeThemeKey) => start({ mode: 'thematic', theme })} />
      )}
      {sheet === 'training' && (
        <Sheet
          title="Entraînement"
          description="20 questions de tous les thèmes, réparties comme à l’examen. Les questions jamais vues et vos erreurs passent d’abord."
          onClose={() => setSheet(null)}
          footer={
            <Button variant="primary" className="h-12 flex-1" loading={starting} onClick={() => start({ mode: 'training', feedback })}>
              Commencer
            </Button>
          }
        >
          <p className="mb-2 text-[13px] font-medium text-heading">Correction</p>
          <Segmented
            full
            label="Moment de la correction"
            value={feedback}
            onChange={setFeedback}
            options={[
              { id: 'each', label: 'Après chaque question' },
              { id: 'end', label: 'À la fin' },
            ]}
          />
        </Sheet>
      )}
      {sheet === 'exam' && (
        <Sheet
          title="Examen blanc"
          description="Dans les conditions de l’examen : 40 questions, 20 secondes par question, sans correction avant la fin."
          onClose={() => setSheet(null)}
          footer={
            <Button variant="primary" className="h-12 flex-1" loading={starting} onClick={() => start({ mode: 'exam' })}>
              Commencer l’examen
            </Button>
          }
        >
          <ul className="grid gap-2 text-[14px] text-body">
            <li>• 35 bonnes réponses sur 40 pour réussir.</li>
            <li>• Quand le temps est écoulé, la réponse cochée part seule.</li>
            <li>• Installez-vous au calme : comptez environ 15 minutes.</li>
          </ul>
        </Sheet>
      )}
    </div>
  );
}
